<template>
    <v-navigation-drawer
        v-model="drawer"
        width="260"
        :mobile-breakpoint="960"
        dark
        fixed
        app
        style="background: #1c1e28;"
    >
        <div class="admin-navigation-drawer" :style="bgStyle">
            <div class="sidebar-container">
                <div class="logo-section">
                    <img v-if="isAllegra" height="40px" src="../../../assets/img/allegra/logoDarkHor.svg" alt="" />
                    <img v-else height="40px" src="../../../assets/img/aria/logoDarkHor.svg" alt="" />
                </div>

                <v-list>
                    <template v-for="menuItem in filteredMenuItems">
                        <v-list-item
                            v-if="!menuItem.children"
                            :key="menuItem.route"
                            class="menu-item"
                            :class="{ selected: $route.name === menuItem.route }"
                            @click="goToRoute(menuItem.route, menuItem.display)"
                        >
                            <v-list-item-icon>
                                <v-badge
                                    v-if="menuItem.needsAttention"
                                    color="error"
                                    icon="fas fa-exclamation"
                                >
                                    <v-icon size="20">{{ menuItem.icon }}</v-icon>
                                </v-badge>

                                <v-icon v-else size="20">{{ menuItem.icon }}</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="menu-item__title">
                                    {{ menuItem.display }}
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>

                        <v-list-group
                            v-else
                            :key="menuItem.section"
                            v-model="menuItem.active"
                            :prepend-icon="menuItem.icon"
                            class="menu-section"
                            no-action
                        >
                            <template v-slot:activator>
                                <v-list-item-title>{{ menuItem.section }}</v-list-item-title>
                            </template>

                            <v-list-item
                                v-for="submenuItem in menuItem.children"
                                :key="submenuItem.route"
                                class="menu-item"
                                @click="goToRoute(submenuItem.route, submenuItem.display)"
                            >
                                <v-list-item-content>
                                    <v-list-item-title
                                        class="menu-item__title"
                                        :class="{ selected: $route.name === submenuItem.route }"
                                    >
                                        {{ submenuItem.display }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list-group>
                    </template>

                </v-list>
            </div>
        </div>
    </v-navigation-drawer>
</template>

<script>
import EinvoiceService from '@/services/EinvoiceService';

export default {
    created() {
        this.menuItems.forEach(menuItem => {
            let activeItem = null;
            if(menuItem.route === this.$route.name){
                activeItem = menuItem;
            } else if(menuItem.children) {
                activeItem = menuItem.children.find(item => item.route === this.$route.name);
            }

            if (activeItem) {
                setTimeout(() => {
                    this.eventBus.$emit('route-update', { title: activeItem.display })
                }, 300)
            }
        });

        this.eventBus.$on('admin-toggle-drawer', this.toggleDrawer);
    },

    beforeDestroy() {
        this.eventBus.$off('admin-toggle-drawer', this.toggleDrawer);
    },

    data() {
        return {
            drawer: this.$vuetify.breakpoint.mdAndUp,
        }
    },

    computed: {
        menuItems() {
            let subscriptionAmountDue = parseFloat(this.tenant?.company?.amount_due)

            return [
                {
                    icon: 'fal fa-chart-pie',
                    display: this.$t('admin_menu.item.dashboard'),
                    route: 'adminDashboard',
                    enabled: this.allows('access_dashboard'),
                    active: this.$route.name === 'adminDashboard'
                },
                {
                    icon: 'fal fa-user',
                    display: this.$t('admin_menu.item.account'),
                    route: 'adminAccount',
                    enabled: this.allows('access_account'),
                    active: this.$route.name === 'adminAccount'
                },
                {
                    icon: 'fal fa-wallet',
                    display: this.$t('admin_menu.item.billing'),
                    route: 'adminBilling',
                    enabled: this.allows('access_account') && (locale === 'me' || (locale === 'sr' && this.tenant.reseller_id == 2)),
                    active: this.$route.name === 'adminBilling',
                    needsAttention: !isNaN(subscriptionAmountDue) && subscriptionAmountDue > 0
                },
                {
                    section: this.$t('admin_menu.section.users'),
                    icon: 'fal fa-users',
                    children: [
                        {
                            display: this.$t('admin_menu.item.users'),
                            route: 'adminUsers',
                            enabled: this.allows('access_users')
                        },
                        {
                            display: this.$t('admin_menu.item.user_roles'),
                            route: 'adminUserRoles',
                            enabled: this.allows('access_user_roles')
                        }
                    ],
                    active: ['adminUsers', 'adminUserRoles'].includes(this.$route.name)
                },
                {
                    section: this.$t('admin_menu.section.inventory'),
                    icon: 'fal fa-cocktail',
                    children: [
                        {
                            display: this.$t('admin_menu.item.products'),
                            route: 'adminProducts',
                            enabled: this.allows('access_products')
                        },
                        {
                            display: this.$t('admin_menu.item.product_categories'),
                            route: 'adminProductCategories',
                            enabled: this.allows('access_product_categories')
                        },
                        {
                            display: this.$t('admin_menu.item.order_item_comments'),
                            route: 'adminOrderItemComments',
                            enabled: this.allows('access_order_item_comments')
                        },
                        {
                            display: this.$t('admin_menu.item.pricelists'),
                            route: 'adminPricelists',
                            enabled: this.allows('access_pricelists') && !this.inventoryAccountingEnabled
                        },
                    ],
                    active: [
                        'adminProducts',
                        'adminProductCategories',
                        'adminOrderItemComments',
                        'adminPricelists',
                    ].includes(this.$route.name)
                },
                {
                    section: this.$t('admin_menu.section.clients'),
                    icon: 'fal fa-handshake',
                    children: [
                        {
                            display: this.$t('admin_menu.item.clients'),
                            route: 'adminClients',
                            enabled: this.allows('access_clients')
                        },
                        {
                            display: this.$t('admin_menu.item.client_transactions'),
                            route: 'adminClientTransactionsSummary',
                            enabled: this.allows('access_client_transactions') && (this.inventoryAccountingEnabled || EinvoiceService.isEnabled())
                        },
                        {
                            display: this.$t('admin_menu.item.bank_statements'),
                            route: 'adminBankStatements',
                            enabled: this.allows('access_bank_statements') && (this.inventoryAccountingEnabled || EinvoiceService.isEnabled())
                        },
                    ],
                    active: [
                        'adminClients',
                        'adminClientTransactionsSummary',
                        'adminBankStatements',
                    ].includes(this.$route.name)
                },
                {
                    section: this.$t('admin_menu.section.inventory_accounting'),
                    icon: 'fal fa-boxes-alt',
                    children: [
                        {
                            display: this.$t('admin_menu.item.receptions'),
                            route: 'adminReceptions',
                            enabled: this.allows('access_receptions') && (!this.inventoryAccountingEnabled || (this.inventoryAccountingEnabled && !isAllegra))
                        },
                        {
                            display: this.$t('admin_menu.item.dispatches'),
                            route: 'adminDispatches',
                            enabled: this.allows('access_dispatches') && (!this.inventoryAccountingEnabled || (this.inventoryAccountingEnabled && !isAllegra))
                        },
                        {
                            display: this.$t('admin_menu.item.beginning_inventories'),
                            route: 'adminBeginningInventories',
                            enabled: this.allows('access_beginning_inventories') && this.inventoryAccountingEnabled
                        },
                        {
                            display: this.$t('admin_menu.item.price_calculations'),
                            route: 'adminPriceCalculations',
                            enabled: this.allows('access_price_calculations') && this.inventoryAccountingEnabled
                        },
                        {
                            display: this.$t('admin_menu.item.price_levelings'),
                            route: 'adminPriceLevelings',
                            enabled: this.allows('access_price_levelings') && this.inventoryAccountingEnabled
                        },
                        {
                            display: this.$t('admin_menu.item.write_offs'),
                            route: 'adminWriteOffs',
                            enabled: this.allows('access_write_offs') && this.inventoryAccountingEnabled
                        },
                        {
                            display: this.$t('admin_menu.item.cash_sales'),
                            route: 'adminCashSales',
                            enabled: this.allows('access_cash_sales') && this.inventoryAccountingEnabled
                        },
                        {
                            display: this.$t('admin_menu.item.stock'),
                            route: 'adminStock',
                            enabled: this.allows('access_stock') && this.inventoryAccountingEnabled
                        },
                        {
                            display: this.$t('admin_menu.item.sales_purchase_ledger'),
                            route: 'adminSalesPurchaseLedger',
                            enabled: this.allows('access_sales_purchase_ledger') && this.inventoryAccountingEnabled
                        }
                    ],
                    active: [
                        'adminReceptions',
                        'adminDispatches',
                        'adminBeginningInventories',
                        'adminPriceCalculations',
                        'adminPriceLevelings',
                        'adminWriteOffs',
                        'adminStock',
                        'adminSalesPurchaseLedger',
                        'adminCashSales',
                    ].includes(this.$route.name)
                },
                {
                    section: !this.noncashOnly
                        ? this.$t('admin_menu.section.reports_receipts')
                        : this.$t('admin_menu.section.reports_receipts_noncash'),
                    icon: 'fal fa-file-chart-line',
                    children: [
                        {
                            display: !this.noncashOnly
                                ? this.$t('admin_menu.item.orders')
                                : this.$t('admin_menu.item.orders_noncash'),
                            route: 'adminOrders',
                            enabled: this.allows('access_orders')
                        },
                        {
                            display: this.$t('admin_menu.item.reports'),
                            route: 'adminReports',
                            enabled: this.allows('access_reports')
                        },
                        {
                            display: this.$t('admin_menu.item.working_hours'),
                            route: 'adminWorkingHours',
                            enabled: this.allows('access_working_hours')
                        }
                    ],
                    active: ['adminOrders', 'adminReports', 'adminWorkingHours'].includes(this.$route.name)
                },
                {
                    section: this.$t('admin_menu.section.settings'),
                    icon: 'fal fa-cog',
                    children: [
                        {
                            display: this.$t('admin_menu.item.tax_rates'),
                            route: 'adminTaxRates',
                            enabled: this.allows('access_tax_rates')
                        },
                        {
                            display: this.$t('admin_menu.item.printers'),
                            route: 'adminPrinters',
                            enabled: this.allows('access_printers')
                        },
                        {
                            display: this.$t('admin_menu.item.barcodes'),
                            route: 'adminBarcodes',
                            enabled: this.allows('access_barcodes')
                        },
                        {
                            display: this.$t('admin_menu.item.shortcuts'),
                            route: 'adminShortcuts',
                            enabled: this.allows('access_shortcuts')
                        },
                        {
                            display: this.$t('admin_menu.item.tables_settings'),
                            route: 'adminTablesSettings',
                            enabled: this.allows('access_tables_settings')
                        },
                        {
                            display: this.$t('admin_menu.item.order_settings'),
                            route: 'adminOrderSettings',
                            enabled: this.allows('access_order_settings')
                        },
                        {
                            display: this.$t('admin_menu.item.terminals'),
                            route: 'adminTerminals',
                            enabled: this.allows('access_terminals')
                        },
                    ],
                    active: [
                        'adminTaxRates',
                        'adminPrinters',
                        'adminShortcuts',
                        'adminBarcodes',
                        'adminTablePlan',
                        'adminSalesInterface',
                        'adminTerminals',
                    ].includes(this.$route.name)
                },
            ];
        },

        filteredMenuItems() {
            return _.cloneDeep(this.menuItems)
                .map(menuItem => {
                    if(menuItem.children){
                        menuItem.children = menuItem.children.filter(item => item.enabled);
                    }

                    return menuItem;
                })
                .filter(menuItem => menuItem.enabled || (menuItem.children && menuItem.children.length));
        },

        inventoryAccountingEnabled() {
            return !!this.tenant.venue?.settings?.use_inventory_accounting;
        },

        bgStyle() {
            if(this.tenant.venue.bg_image) {
                return { backgroundImage: 'none' }
            }

            return { backgroundImage: `url('/img/${this.isAllegra ? 'allegra' : 'aria'}/background.png')` }
        },

        noncashOnly() {
            return !!this.tenant?.venue?.settings?.noncash_only
        },
    },

    methods: {
        toggleDrawer() {
            this.drawer = !this.drawer;
        },

        goToRoute(route, display) {
            this.eventBus.$emit('route-update', { title: display });
            this.$router.push({ name: route }).catch(err => {});
        }
    }
};
</script>

<style lang="scss">
.admin-navigation-drawer {
    background-color: #101219;
    background-position: bottom left;
    background-repeat: no-repeat;
    background-size: cover;
    height: 100%;
    position: relative;
    z-index: 1;

    &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background: var(--v-admin_sidebar_bg-base);
        opacity: 0.7;
        z-index: -1;
    }

    .sidebar-container {
        height: 100%;

        .v-list {
            background: transparent;
        }

        .logo-section {
            color: white;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 50px;
            padding: 5px;

            img {
                height: 35px;
                z-index: 1;
            }

            @media screen and (min-width: 960px) {
                height: 63px;
            }
        }

        .menu-section {
            .v-list-item--active {
                color: var(--v-admin_sidebar_text-base);
            }

            &.v-list-group--active {
                border-left: 4px solid var(--v-admin_sidebar_text_selected-base);
                background: var(--v-admin_sidebar_bg_expanded-base);
            }

            .v-list-group__header {
                .v-list-group__header__prepend-icon {
                    margin: 14px 25px 0px 0px;
                    .v-icon {
                        font-size: 20px;
                    }
                }

                .v-list-group__header__append-icon {
                    margin-left: 0;
                }
            }

            .menu-item {
                padding-left: 72px !important;

                .menu-item__title {
                    &.selected {
                        color: var(--v-admin_sidebar_text_selected-base) !important;
                    }
                }
            }
        }

        .menu-item {
            .v-list-item__icon {
                margin-right: 23px !important;
            }

            &.selected {
                color: var(--v-admin_sidebar_text_selected-base) !important;
                background: var(--v-admin_sidebar_bg_selected-base) !important;
            }
        }
    }
}
</style>
